import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import {
  getCourseConfig,
  isActCourse,
  isDatCourse,
  isLsatCourse,
  isMcatCourse,
  isSatCourse,
  isStdTestCourse,
} from 'my-core/course-utils';
import { loadOneTapSignIn } from 'my-core/google-sign-in';
import {
  accountSettingsPath,
  actLandingPath,
  actTutoringLandingPath,
  aiTutorPath,
  coursesPath,
  datEliteLandingPath,
  datLandingPath,
  datSelfPacedLandingPath,
  datTutoringLandingPath,
  lsatLandingPath,
  lsatSelfPacedLandingPath,
  lsatTutoringLandingPath,
  mcatLandingPath,
  mcatScoresPath,
  medSchoolAdmissionsLandingPath,
  medSchoolCalculatorPath,
  preMedPlusLandingPath,
  preMedTutoringLandingPath,
  satLandingPath,
  satTutoringLandingPath,
  studentActDashboardPath,
  studentCoursePagePath,
  studentDashboardPath,
  studentDatDashboardPath,
  studentLsatDashboardPath,
  studentMcatDashboardPath,
  studentSatDashboardPath,
  studentSignupPath,
} from 'my-core/routes';
import { actIcon, datIcon, lsatIcon, mcatIcon, satIcon, SCHOOL_TYPES_CONFIG } from 'my-core/school-utils';
import { SITE_TOURS } from 'my-core/site-tour';
import { capitalize, throttle } from 'my-utils';

import Loadable from 'my-components/Loadable';
import { BANNER_TRANSITION_DURATION } from 'my-components/Root/SiteWideBanner';
import SiteTourStep from 'my-components/SiteTourStep';
// import AppBarSearchMenu from './AppBarSearchMenu';
// import SearchMenu from 'my-components/SearchMenu';
import MorphFontAwesomeIcon from 'my-elements/MorphFontAwesomeIcon';
import MuiAvatar from 'my-elements/MuiAvatar';
import PopoverResponsive from 'my-elements/PopoverResponsive';

import { PRODUCT_PAGES_CONFIG } from './app-bar-data';
// import AppBarTextbooksLink from './AppBarTextbooksLink';
import Logo from './AppBarLogo';
import AppBarNavLink from './AppBarNavLink';
import UserMenu from './UserMenu';

import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  faBars,
  faBell,
  faBooks,
  faComment,
  faCompass,
  faSparkles,
  faSun,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { faCaretDown, faMoon, faTombstone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PREFIX = 'MyAppBar';

const classes = {
  root_light: `${PREFIX}-root_light`,
  root_color: `${PREFIX}-root_color`,
  root_medium: `${PREFIX}-root_medium`,
  root_dark: `${PREFIX}-root_dark`,
  root_siteBannerActive: `${PREFIX}-root_siteBannerActive`,
  toolbar: `${PREFIX}-toolbar`,
  logoTick: `${PREFIX}-logoTick`,
  logoDash: `${PREFIX}-logoDash`,
  navLinkWrapper: `${PREFIX}-navLinkWrapper`,
  navLink: `${PREFIX}-navLink`,
  navLink_underline: `${PREFIX}-navLink_underline`,
  navLink_open: `${PREFIX}-navLink_open`,
  navLink_active: `${PREFIX}-navLink_active`,
  button: `${PREFIX}-button`,
  button_text: `${PREFIX}-button_text`,
  button_contained: `${PREFIX}-button_contained`,
  spacer: `${PREFIX}-spacer`,
  whiteColorOnNonLight: `${PREFIX}-whiteColorOnNonLight`,
  marginLeft: `${PREFIX}-marginLeft`,
  marginRight: `${PREFIX}-marginRight`,
};

const appBarCss = ({ breakpoints, constants, palette, spacing, transitions: { create, duration }, typography }) => {
  const defaultTransitionProps = { duration: duration.standard };
  return {
    height: constants.APPBAR_HEIGHT,
    backgroundColor: 'transparent',
    marginTop: 0,
    transition: [
      create('margin-top', { duration: BANNER_TRANSITION_DURATION }),
      create('height', { easing: 'ease-in' }),
    ].join(', '),

    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: palette.background.paper,
      borderBottom: `1px solid ${palette.divider}`,
      transition: create('opacity', defaultTransitionProps),
    },
    [`&.${classes.root_light}, &.${classes.root_color}, &.${classes.root_medium}, &.${classes.root_dark}`]: {
      '&:before': { opacity: 0 },
    },

    // don't need appbar when printing (ie. course-instructors/:id/contract)
    '@media print': { display: 'none' },
    [`&.${classes.root_siteBannerActive}`]: { marginTop: constants.APPBAR_HEIGHT },
    // root_searchFocused: {
    //   height: 90,
    //   transition: [create('margin-top'), create('height', { delay: duration.shorter, easing: 'ease-out' })]
    // },

    [`.${classes.toolbar}`]: {
      minHeight: constants.APPBAR_HEIGHT,
      [breakpoints.down('sm')]: { padding: spacing(0, 1) },
    },
    [`.${classes.logoTick}`]: { transition: create(['fill'], defaultTransitionProps) },
    [`&.${classes.root_dark} .${classes.logoTick}, &.${classes.root_color} .${classes.logoTick}`]: {
      fill: palette.common.white,
    },
    [`&.${classes.root_medium} .${classes.logoTick}`]: { fill: palette.grey[700] },
    [`.${classes.logoDash}`]: { transition: create(['fill'], defaultTransitionProps) },
    [`&.${classes.root_color} .${classes.logoDash}`]: { fill: palette.common.white },

    [`.${classes.navLinkWrapper}`]: {
      alignSelf: 'stretch',
      flex: '0 0 auto',
      display: 'flex',
    },
    [`.${classes.navLink}`]: {
      ...typography.button,
      display: 'flex',
      alignItems: 'center',
      color: palette.text.primary,
      padding: spacing(1, '1.5vw'),
      transition: create(['color'], defaultTransitionProps),
      [`&.${classes.navLink_active}:not(.${classes.navLink_underline}), &:hover`]: {
        backgroundColor: 'transparent', // for dropdown btn
        color: palette.text.secondary,
      },
      [`&.${classes.navLink_active}`]: { color: palette.primary.main },
      '> svg': {
        [breakpoints.down(370)]: { display: 'none' },
        marginLeft: spacing(0.5),
        fontSize: 12,
        transition: create(['transform'], { duration: duration.shorter }),
      },
      [breakpoints.down('sm')]: { fontSize: 13 },
    },
    [`&.${classes.root_dark} .${classes.navLink}, &.${classes.root_color} .${classes.navLink}`]: {
      color: palette.common.white,
      [`&.${classes.navLink_active}:not(.${classes.navLink_underline}), &:hover`]: { color: palette.text.secondary },
      [`&.${classes.navLink_active}`]: { color: palette.common.white },
    },
    [`&.${classes.root_medium} .${classes.navLink}`]: {
      color: palette.common.black,
      [`&.${classes.navLink_active}:not(${classes.navLink_underline}), &:hover`]: { color: 'rgba(0,0,0,0.5)' },
      [`&.${classes.navLink_active}`]: { color: palette.common.black },
    },
    [`.${classes.navLink_underline}`]: {
      position: 'relative',
      [`&.${classes.navLink_active}::before`]: { transform: 'scaleX(1)' },
      '&::before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '1vw',
        right: '1vw',
        height: 3,
        backgroundColor: 'currentColor',
        transform: 'scaleX(0)',
        transition: create(['transform'], defaultTransitionProps),
      },
    },
    [`.${classes.navLink_open}`]: {
      '& svg': { transform: 'rotate(180deg)' },
    },
    [`.${classes.button}`]: {
      flex: '0 0 auto',
      minWidth: 0,
      [breakpoints.down('sm')]: { fontSize: 13 },
    },
    [`.${classes.button_text}`]: {
      transition: create(['color'], defaultTransitionProps),
    },
    [`&.${classes.root_color} .${classes.button_text}, &.${classes.root_medium} .${classes.button_text}, &.${classes.root_dark} .${classes.button_text}`]:
      {
        color: palette.common.white,
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
      },
    [`.${classes.button_contained}`]: {
      [breakpoints.down('sm')]: { padding: spacing(0.5, 1.5) },
    },

    [`&.${classes.root_color} .${classes.button_contained}, &.${classes.root_medium} .${classes.button_contained}`]: {
      backgroundColor: palette.common.white,
      color: palette.primary.main,
      transition: create(['background-color', 'color'], defaultTransitionProps),
      '&:hover': { backgroundColor: palette.grey[200] },
    },

    [`.${classes.spacer}`]: { flex: '1 1 0' },

    // this will change the color when the app bar is transparent and background is not 'light'
    [`.${classes.whiteColorOnNonLight}`]: { transition: create(['color'], defaultTransitionProps) },
    [`&.${classes.root_dark} .${classes.whiteColorOnNonLight}, &.${classes.root_color} .${classes.whiteColorOnNonLight}`]:
      {
        color: palette.common.white,
      },
    [`&.${classes.root_medium} .${classes.whiteColorOnNonLight}`]: {
      color: palette.common.black,
    },

    [`.${classes.marginLeft}`]: { marginLeft: spacing(1) },
    [`.${classes.marginRight}`]: { marginRight: spacing(1) },
  };
};

const MessagesMenu = Loadable(() => import(/* webpackChunkName: "messages-menu" */ './MessagesMenu'), {
  hideLoader: true,
});
const NotificationMenu = Loadable(() => import(/* webpackChunkName: "notification-menu" */ './NotificationMenu'), {
  hideLoader: true,
});

export const APPBAR_SIDEDRAWER_CONTAINER_ID = 'appbar_prepend_placeholder';

const THEME_MODE_ICONS = { light: faSun, dark: faMoon, halloween: faTombstone };
const THEME_MODES = Object.keys(THEME_MODE_ICONS);

// The signed-out bar needs about 407px for the logo, both links, the signup button and the menu button. Below this
// width a guest gets only the logo, signup button and menu button, and the links move into the menu.
const NARROW_GUEST_MAX_WIDTH = 420;

export default function MyAppBar(props) {
  const {
    color,
    currentUser,
    hasChats,
    homePath,
    location,
    onChangeThemeMode,
    onGoogleLoginSuccess,
    onSignOut,
    onStartSiteTour,
    onToggleLoginForm,
    onToggleSignUpForm,
    routes,
    siteBannerActive,
    subscription,
    themeMode,
  } = props;

  const rootRef = useRef(null);

  const isXsOnly = useMediaQuery(({ breakpoints }) => breakpoints.only('xs'));
  const isLgCollapsed = useMediaQuery(({ breakpoints }) => breakpoints.down(1100)); // based on guest user nav (most links)
  const isMdCollapsed = useMediaQuery(({ breakpoints }) => breakpoints.down(730)); // based on guest user nav (most links)
  const isNarrowGuestMatch = useMediaQuery(({ breakpoints }) => breakpoints.down(NARROW_GUEST_MAX_WIDTH));
  const isNarrowGuest = isNarrowGuestMatch && currentUser.guest;
  const nextThemeMode = THEME_MODES[(THEME_MODES.indexOf(themeMode) + 1) % THEME_MODES.length];

  const [isScrolled, setIsScrolled] = useState(false);
  // attach event handler scroll event
  useEffect(() => {
    const throttledScrollHandler = throttle(() => {
      const scrollTop = (document.scrollingElement || document.body).scrollTop;
      setIsScrolled(scrollTop > 55);
    }, 200);

    window.addEventListener('scroll', throttledScrollHandler);
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, []);

  const [signUpText, loginProps, loginButtonProps, signupButtonProps] = useMemo(() => {
    if (currentUser.guest) {
      const currentPageProduct = PRODUCT_PAGES_CONFIG.find(p => p.regex.test(location.pathname));
      const isTeacherPage = location.pathname.endsWith('for-educators');
      const loginProps = {
        role: isTeacherPage ? 'teacher' : undefined,
        messagingProduct: currentPageProduct?.product,
      };
      return [
        'Get Started for Free',
        loginProps,
        { onClick: () => onToggleLoginForm(loginProps) },
        currentPageProduct ?
          {
            path: studentSignupPath({
              product: currentPageProduct.product,
              redirect: currentPageProduct.loginRedirect,
            }),
          }
        : { onClick: () => onToggleSignUpForm(loginProps) },
      ];
    }
    return [];
  }, [currentUser.guest, location.pathname, onToggleLoginForm, onToggleSignUpForm]);

  useEffect(() => {
    if (currentUser.guest) {
      const { redirect, role } = loginProps;
      loadOneTapSignIn(
        response => onGoogleLoginSuccess(response, redirect, role),
        () => console.error('one-tap google sign in error'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to fire one time
  }, []);

  // const [searchFocused, setSearchFocused] = useState(false);
  // const colorVariant = isScrolled || searchFocused ? '' : color;

  const colorVariant = isScrolled ? '' : color;

  // const isCanadian = (currentUser.school?.country_code || currentUser.country_code) === 'CA';
  const schoolType = currentUser.school?.type;
  const isPreMedPlusSubscriber = subscription?.is_premed_plus;
  const courseLinks = useMemo(() => {
    const currentCourses = currentUser.user_courses.filter(uc => !uc.is_past && !isStdTestCourse(uc.course));
    const collapseCourseSearchLinks = !!currentUser.user_courses.length || !!routes.main?.length;
    const isMcatStudent = isPreMedPlusSubscriber || currentUser.user_courses.some(uc => isMcatCourse(uc.course));
    const isLsatStudent = currentUser.user_courses.some(uc => isLsatCourse(uc.course));
    const isDatStudent = currentUser.user_courses.some(uc => isDatCourse(uc.course));
    const isSatStudent = currentUser.user_courses.some(uc => isSatCourse(uc.course));
    const isActStudent = currentUser.user_courses.some(uc => isActCourse(uc.course));

    const isPartialCollapse = isLgCollapsed && !isMdCollapsed;

    const highSchoolLinks = [
      (!schoolType || schoolType === 'high_school' || isSatStudent) && {
        icon: satIcon,
        label: 'SAT',
        skipMatching: true,
        ...(isSatStudent ?
          {
            path: studentSatDashboardPath(),
            subLabel: 'SAT Dashboard',
            items: [{ path: satLandingPath(), label: 'SAT Elite 1500' }, ...SAT_EXTRA_LINKS],
          }
        : {
            path: satLandingPath(),
            subLabel: 'SAT Elite 1500',
            items: SAT_EXTRA_LINKS,
          }),
      },
      (!schoolType || schoolType === 'high_school' || isActStudent) && {
        icon: actIcon,
        label: 'ACT',
        skipMatching: true,
        ...(isActStudent ?
          {
            path: studentActDashboardPath(),
            subLabel: 'ACT Dashboard',
            items: [{ path: actLandingPath(), label: 'ACT Elite 33' }, ...ACT_EXTRA_LINKS],
          }
        : {
            path: actLandingPath(),
            subLabel: 'ACT Elite 33',
            items: ACT_EXTRA_LINKS,
          }),
      },
    ].filter(Boolean);

    const undergradLinks = [
      (!schoolType || schoolType === 'undergrad' || isMcatStudent) && {
        icon: mcatIcon,
        label: isMcatStudent ? 'Pre-Med' : 'MCAT',
        skipMatching: true,
        ...(isMcatStudent ?
          {
            path: studentMcatDashboardPath(),
            subLabel: 'MCAT Dashboard',
            items: [{ path: mcatLandingPath(), label: 'MCAT Elite 515' }, ...MCAT_EXTRA_LINKS],
          }
        : {
            path: mcatLandingPath(),
            subLabel: 'MCAT Elite 515',
            items: MCAT_EXTRA_LINKS,
          }),
      },
      (!schoolType || schoolType === 'undergrad' || isLsatStudent) && {
        icon: lsatIcon,
        label: 'LSAT',
        skipMatching: true,
        ...(isLsatStudent ?
          {
            path: studentLsatDashboardPath(),
            subLabel: 'LSAT Dashboard',
            items: [{ path: lsatLandingPath(), label: 'LSAT Elite 170' }, ...LSAT_EXTRA_LINKS],
          }
        : {
            path: lsatLandingPath(),
            subLabel: 'LSAT Elite 170',
            items: LSAT_EXTRA_LINKS,
          }),
      },
      (!schoolType || schoolType === 'undergrad' || isDatStudent) && {
        icon: datIcon,
        label: 'DAT',
        skipMatching: true,
        ...(isDatStudent ?
          {
            path: studentDatDashboardPath(),
            subLabel: 'DAT Dashboard',
            items: [{ path: datLandingPath(), label: 'DAT Programs' }, ...DAT_EXTRA_LINKS],
          }
        : {
            path: datLandingPath(),
            subLabel: 'DAT Programs',
            items: DAT_EXTRA_LINKS,
          }),
      },
    ].filter(Boolean);
    return [
      !currentUser.guest && {
        icon: faBooks,
        path: studentDashboardPath(),
        label: 'My Courses',
        subLabel: 'My Courses Dashboard',
        items:
          collapseCourseSearchLinks ?
            [
              ...currentCourses.slice(0, 5).map(uc => ({
                icon: getCourseConfig(uc.course).faIcon,
                path: studentCoursePagePath(uc.course),
                label: uc.course.code,
              })),
              {
                icon: SCHOOL_TYPES_CONFIG.undergrad.icon,
                path: coursesPath({ school_type: 'undergrad' }),
                skipMatching: true,
                label: 'Browse University Courses',
              },
              {
                icon: SCHOOL_TYPES_CONFIG.high_school.icon,
                path: coursesPath({ school_type: 'high_school' }),
                skipMatching: true,
                label: 'Browse High School Courses',
              },
            ]
          : undefined,
      },
      !collapseCourseSearchLinks && {
        icon: SCHOOL_TYPES_CONFIG.high_school.icon,
        path: coursesPath({ school_type: 'high_school' }),
        label: 'High School',
        subLabel: 'High School Courses',
        items: isPartialCollapse ? highSchoolLinks : undefined,
      },
      ...(collapseCourseSearchLinks || !isPartialCollapse ? highSchoolLinks : []),
      !collapseCourseSearchLinks && {
        icon: SCHOOL_TYPES_CONFIG.undergrad.icon,
        path: coursesPath({ school_type: 'undergrad' }),
        label: 'University',
        subLabel: 'University Courses',
        items: isPartialCollapse ? undergradLinks : undefined,
      },
      ...(collapseCourseSearchLinks || !isPartialCollapse ? undergradLinks : []),
    ].filter(Boolean);
  }, [
    currentUser.guest,
    currentUser.user_courses,
    schoolType,
    isPreMedPlusSubscriber,
    routes.main?.length,
    isMdCollapsed,
    isLgCollapsed,
  ]);

  const navLinkClasses = {
    rootWrapper: classes.navLinkWrapper,
    root: classes.navLink,
    root_active: classes.navLink_active,
    root_underline: classes.navLink_underline,
    root_open: classes.navLink_open,
  };

  return (
    <AppBar
      className={classNames(classes[`root_${colorVariant}`], {
        // [classes.root_searchFocused]: searchFocused,
        [classes.root_siteBannerActive]: siteBannerActive,
      })}
      color="inherit"
      css={appBarCss}
      elevation={0}
      ref={rootRef}
    >
      <Toolbar className={classes.toolbar}>
        <Logo logoDashClassName={classes.logoDash} logoTickClassName={classes.logoTick} to={homePath} />
        <div id={APPBAR_SIDEDRAWER_CONTAINER_ID} />
        {!isNarrowGuest && (
          <AppBarNavLink classes={navLinkClasses} label="AI Tutor" location={location} path={aiTutorPath()} />
        )}
        {isNarrowGuest ?
          null
        : isMdCollapsed ?
          <AppBarNavLink
            classes={navLinkClasses}
            items={courseLinks}
            label="Courses"
            location={location}
            // underline={!currentUser.guest}
          />
        : [...(routes.main || []), ...courseLinks].map(({ icon, items, label, path, subLabel }, idx) => (
            <AppBarNavLink
              key={idx}
              classes={navLinkClasses}
              icon={icon}
              items={items}
              label={label}
              location={location}
              path={path}
              subLabel={subLabel}
              underline={!currentUser.guest}
            />
          ))
        }
        {/* {currentUser.guest && <AppBarTextbooksLink classes={navLinkClasses} pathname={location.pathname} />} */}
        {/*location.pathname.startsWith('/search') ? null : isXsOnly ? (
          <SearchMenu
            classes={{ root: classNames(classes.marginRight, classes.marginLeft) }}
            colorVariant={colorVariant}
            currentUser={currentUser}
          />
        ) : (
          <AppBarSearchMenu
            className={classNames(classes.marginRight, classes.marginLeft)}
            currentUser={currentUser}
            focused={searchFocused}
            onFocus={setSearchFocused}
          />
        )*/}
        <div className={classes.spacer} />
        {renderDarkModeToggle()}
        {renderSiteTourStep()}
        {renderFixedItems()}
      </Toolbar>
    </AppBar>
  );

  /**
   *
   * these render functions don't do much but they help break out some of the
   * logic so it is clear what this component actually renders
   *
   */

  function renderDarkModeToggle() {
    if (!isXsOnly)
      return (
        <Tooltip title={`Enable ${capitalize(nextThemeMode)} mode`}>
          <IconButton className={classes.whiteColorOnNonLight} onClick={() => onChangeThemeMode(nextThemeMode)}>
            <MorphFontAwesomeIcon icon={THEME_MODE_ICONS[themeMode]} size="sm" />
          </IconButton>
        </Tooltip>
      );
  }

  function renderSiteTourStep() {
    if (!onStartSiteTour) return;

    return (
      <SiteTourStep step={SITE_TOURS.SITE_TOUR_HELP.steps.final} tourId={SITE_TOURS.SITE_TOUR_HELP.id}>
        {id => (
          <Tooltip title="Site Tour">
            <IconButton className={classes.whiteColorOnNonLight} id={id} onClick={onStartSiteTour}>
              <FontAwesomeIcon icon={faCompass} size="sm" />
            </IconButton>
          </Tooltip>
        )}
      </SiteTourStep>
    );
  }

  function renderFixedItems() {
    const darkModeToggleItem = isXsOnly && [
      {
        icon: THEME_MODE_ICONS[themeMode],
        label: `Enable ${capitalize(nextThemeMode)} Mode`,
        onClick: () => onChangeThemeMode(nextThemeMode),
      },
    ];
    if (currentUser.guest) {
      return (
        <>
          {!isXsOnly && (
            <Button
              className={classNames(classes.button, classes.button_text)}
              id="appbar_login"
              {...(loginButtonProps.path ?
                { component: Link, nativeButton: false, to: loginButtonProps.path }
              : loginButtonProps)}
              variant="text"
            >
              Log in
            </Button>
          )}
          <Button
            className={classNames(classes.button, classes.button_contained, classes.marginLeft)}
            id="appbar_signup"
            {...(signupButtonProps.path ?
              { component: Link, nativeButton: false, to: signupButtonProps.path }
            : signupButtonProps)}
            variant="contained"
          >
            {signUpText}
          </Button>
          {isLgCollapsed && (
            <PopoverResponsive
              button={({ open, ref, setOpen }) => (
                <IconButton
                  className={classNames(classes.marginLeft, classes.whiteColorOnNonLight)}
                  onClick={() => setOpen(!open)}
                  ref={ref}
                >
                  <FontAwesomeIcon icon={open ? faTimes : faBars} size="sm" />
                </IconButton>
              )}
              component={UserMenu}
              componentProps={{
                location,
                navLinks: isNarrowGuest && [
                  { icon: faSparkles, label: 'AI Tutor', path: aiTutorPath() },
                  ...courseLinks,
                ],
                items: [
                  routes.main?.flatMap(r => r.paths || r),
                  routes.resources,
                  darkModeToggleItem,
                  isXsOnly && [
                    { button: 'outlined', label: 'Log in', ...loginButtonProps },
                    { button: 'contained', label: signUpText, ...signupButtonProps },
                  ],
                ].filter(Boolean),
              }}
            />
          )}
        </>
      );
    }

    return (
      <>
        {hasChats && (
          <MessagesMenu
            className={classes.whiteColorOnNonLight}
            colorVariant={colorVariant}
            fallback={
              <IconButton className={classes.whiteColorOnNonLight} disabled>
                <FontAwesomeIcon icon={faComment} size="sm" />
              </IconButton>
            }
            isXS={isXsOnly}
            userId={currentUser.id}
          />
        )}
        <NotificationMenu
          className={classes.whiteColorOnNonLight}
          colorVariant={colorVariant}
          currentUser={currentUser}
          fallback={
            <IconButton className={classes.whiteColorOnNonLight} disabled>
              <FontAwesomeIcon icon={faBell} size="sm" />
            </IconButton>
          }
        />
        <PopoverResponsive
          button={
            <Button
              className={classNames(classes.button, classes.whiteColorOnNonLight)}
              color="inherit"
              size="small"
              variant="text"
            >
              <FontAwesomeIcon className={classes.marginRight} icon={faCaretDown} />
              <MuiAvatar alt="more links" size={30} src="thumb" user={currentUser} />
            </Button>
          }
          component={UserMenu}
          componentProps={{
            items: [
              [{ path: accountSettingsPath(), label: 'My Account' }],
              darkModeToggleItem,
              isMdCollapsed && routes.main?.flatMap(r => r.paths || r).filter(Boolean),
              routes.role,
              routes.resources,
              [{ button: 'outlined', label: 'Sign Out', onClick: onSignOut }],
            ].filter(Boolean),
          }}
        />
      </>
    );
  }
}

const SAT_EXTRA_LINKS = [{ path: satTutoringLandingPath(), label: 'SAT Tutoring' }];
const ACT_EXTRA_LINKS = [{ path: actTutoringLandingPath(), label: 'ACT Tutoring' }];
const MCAT_EXTRA_LINKS = [
  { path: medSchoolAdmissionsLandingPath(), label: 'Med-School Admissions' },
  { path: medSchoolCalculatorPath(), label: 'Med School Calculator' },
  { path: mcatScoresPath(), label: 'MCAT Score Percentiles' },
  { path: preMedTutoringLandingPath(), label: 'Pre-Med Tutoring' },
  { path: preMedPlusLandingPath(), label: 'Pre-Med Plus' },
];
const LSAT_EXTRA_LINKS = [
  { path: lsatSelfPacedLandingPath(), label: 'LSAT Self-Paced' },
  { path: lsatTutoringLandingPath(), label: 'LSAT Tutoring' },
];
const DAT_EXTRA_LINKS = [
  { path: datEliteLandingPath(), label: 'DAT Elite' },
  { path: datSelfPacedLandingPath(), label: 'DAT Self-Paced' },
  { path: datTutoringLandingPath(), label: 'DAT Tutoring' },
];
